import { logger } from '@shared/lib/utils/logger'
/**
 * Cookie / analytics consent storage
 * Persists the visitor's tracking-consent decision so PostHog capture stays
 * opted-out until the user explicitly accepts.
 *
 * Re-prompt policy:
 *  - 'granted' never expires — an accepting user is not asked again.
 *  - 'denied' expires after DENIED_RECHECK_MS (3 weeks); once expired,
 *    getConsent() returns null so the banner shows again.
 */

export type ConsentDecision = 'granted' | 'denied' | 'dismissed'

const CONSENT_KEY = 'professionals_cookie_consent'

// Re-ask a user who denied after this long. Granted decisions never expire.
const DENIED_RECHECK_MS = 21 * 24 * 60 * 60 * 1000 // 3 weeks

interface StoredConsent {
  decision: ConsentDecision
  ts: number
}

export const consentStorage = {
  /**
   * Get the effective consent decision, applying the re-check policy.
   * Returns null when the user hasn't decided yet OR a prior 'denied' has expired,
   * in both cases the banner should be shown again.
   */
  getConsent(): ConsentDecision | null {
    try {
      const raw = localStorage.getItem(CONSENT_KEY)
      if (!raw) return null

      // Current format: JSON { decision, ts }. Legacy format (pre-recheck): a bare
      // 'granted'/'denied' string with no timestamp.
      let parsed: StoredConsent | null = null
      try {
        const obj = JSON.parse(raw) as Partial<StoredConsent>
        if (
          obj &&
          (obj.decision === 'granted' ||
            obj.decision === 'denied' ||
            obj.decision === 'dismissed')
        ) {
          parsed = { decision: obj.decision, ts: typeof obj.ts === 'number' ? obj.ts : 0 }
        }
      } catch {
        if (raw === 'granted' || raw === 'denied') parsed = { decision: raw, ts: 0 }
      }
      if (!parsed) return null

      // Granted never expires.
      if (parsed.decision === 'granted') return 'granted'

      // Denied/dismissed expires after the recheck window. Legacy denials (ts:0) are always
      // expired → user gets re-prompted once, then stored in the new format.
      if (Date.now() - parsed.ts >= DENIED_RECHECK_MS) return null
      return parsed.decision
    } catch (error) {
      logger.error('Failed to load cookie consent:', error)
      return null
    }
  },

  /**
   * Persist the consent decision with the current timestamp.
   */
  setConsent(decision: ConsentDecision): void {
    try {
      const payload: StoredConsent = { decision, ts: Date.now() }
      localStorage.setItem(CONSENT_KEY, JSON.stringify(payload))
    } catch (error) {
      logger.error('Failed to save cookie consent:', error)
    }
  },
}
